import { useState } from 'react'
import { CalendarClock, Loader2, StickyNote } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useBlocoColleagues } from '@/hooks/useBlocoColleagues'
import { BlocoColleagueSelect } from './BlocoColleagueSelect'
import { BlocoAttachmentField } from './BlocoAttachmentField'
import type { BlocoType } from '@/types/bloco'

interface BlocoComposerProps {
  saving: boolean
  currentUserId?: string
  onSubmit: (
    payload: {
      title: string
      subject: string
      type: BlocoType
      dueAt?: string | null
      dueTime?: string | null
      memberUserIds?: string[]
    },
    attachments?: File[],
  ) => Promise<unknown>
}

const fieldClass =
  'w-full rounded-lg border border-[var(--glass-border)] bg-bg-card px-3 py-2 text-sm text-text-primary outline-none placeholder:text-text-muted focus:border-[var(--brand-primary)]'

function normalizeDueTime(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const match = trimmed.match(/^([01]\d|2[0-3]):([0-5]\d)/)
  return match ? `${match[1]}:${match[2]}` : null
}

export function BlocoComposer({ saving, currentUserId, onSubmit }: BlocoComposerProps) {
  const [title, setTitle] = useState('')
  const [subject, setSubject] = useState('')
  const [type, setType] = useState<BlocoType>('note')
  const [dueDate, setDueDate] = useState('')
  const [dueTime, setDueTime] = useState('')
  const [memberUserIds, setMemberUserIds] = useState<string[]>([])
  const [attachments, setAttachments] = useState<File[]>([])
  const { colleagues, loading: colleaguesLoading } = useBlocoColleagues(true)

  const canSubmit = title.trim().length > 0 && subject.trim().length > 0 && (type !== 'return' || dueDate)

  const handleSubmit = async () => {
    if (!canSubmit) return

    await onSubmit(
      {
        title: title.trim(),
        subject: subject.trim(),
        type,
        dueAt: type === 'return' && dueDate ? new Date(`${dueDate}T12:00:00`).toISOString() : null,
        dueTime: type === 'return' ? normalizeDueTime(dueTime) : null,
        memberUserIds: memberUserIds.length ? memberUserIds : undefined,
      },
      attachments,
    )

    setTitle('')
    setSubject('')
    setType('note')
    setDueDate('')
    setDueTime('')
    setMemberUserIds([])
    setAttachments([])
  }

  return (
    <div className='rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)] p-4'>
      <div className='grid gap-3 sm:grid-cols-2'>
        <label className='block text-xs font-medium text-text-muted'>
          Título *
          <input
            type='text'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder='Ex.: Follow-up proposta X'
            maxLength={200}
            className={`${fieldClass} mt-1`}
          />
        </label>

        <label className='block text-xs font-medium text-text-muted'>
          Assunto *
          <input
            type='text'
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder='Ex.: Validar escopo dos layouts'
            maxLength={200}
            className={`${fieldClass} mt-1`}
          />
        </label>
      </div>

      <div className='mt-3 flex flex-wrap items-center gap-2'>
        <button
          type='button'
          onClick={() => setType('note')}
          className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
            type === 'note'
              ? 'bg-[var(--brand-primary-muted)] text-[var(--brand-primary)]'
              : 'text-text-muted hover:bg-[var(--glass-bg)]'
          }`}
        >
          <StickyNote size={14} />
          Nota
        </button>
        <button
          type='button'
          onClick={() => setType('return')}
          className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
            type === 'return'
              ? 'bg-[var(--brand-primary-muted)] text-[var(--brand-primary)]'
              : 'text-text-muted hover:bg-[var(--glass-bg)]'
          }`}
        >
          <CalendarClock size={14} />
          Retorno
        </button>
      </div>

      {type === 'return' && (
        <div className='mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2'>
          <label className='text-xs text-text-muted'>
            Data *
            <input
              type='date'
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              required
              className='mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-bg-card px-2 py-1.5 text-sm text-text-primary outline-none focus:border-[var(--brand-primary)]'
            />
          </label>
          <label className='text-xs text-text-muted'>
            Hora (opcional)
            <input
              type='time'
              value={dueTime}
              onChange={(e) => setDueTime(e.target.value)}
              className='mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-bg-card px-2 py-1.5 text-sm text-text-primary outline-none focus:border-[var(--brand-primary)]'
            />
          </label>
        </div>
      )}

      <BlocoAttachmentField files={attachments} onChange={setAttachments} disabled={saving} />

      <BlocoColleagueSelect
        colleagues={colleagues}
        selectedIds={memberUserIds}
        currentUserId={currentUserId}
        loading={colleaguesLoading}
        onChange={setMemberUserIds}
      />

      <div className='mt-3 flex justify-end'>
        <Button
          size='sm'
          onClick={() => void handleSubmit()}
          disabled={saving || !canSubmit}
          icon={saving ? <Loader2 size={14} className='animate-spin' /> : undefined}
        >
          {saving ? 'Salvando…' : 'Adicionar bloco'}
        </Button>
      </div>
    </div>
  )
}
